import React, { useState } from 'react'
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogActions,
  Textarea,
  Label
} from '@fluentui/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar } from '@fortawesome/free-solid-svg-icons'
import styled from 'styled-components'
import Button from '../Button/Button'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { submitFeedback } from '../../services/api'
import Toast, { ToastIntentEnum } from '../Toast/Toast'

const MAX_STARS = 5

export type FeedbackFormData = {
  comment: string
  satisfactionScore: number
  question: string
}

const feedbackSchema = yup.object().shape({
  comment: yup.string().required('Comment is required'),
  satisfactionScore: yup
    .number()
    .min(1, 'Rating is required')
    .required('Rating is required'),
  question: yup.string().required('Survey question is required')
})

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 9999;
`

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: #fff;
  padding: 2rem;
  max-width: 500px;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 1rem;
`

const StarsContainer = styled.div`
  display: flex;
  padding-top: 5px;
  gap: 7px;
  cursor: pointer;
`

const FeedbackModal: React.FC<{
  setShowForm: (show: boolean) => void
  surveyQuestion: string
}> = ({ setShowForm, surveyQuestion }) => {
  const [toastInfo, setToastInfo] = useState<{
    show: boolean
    intent: ToastIntentEnum
    message: string
  }>({ show: false, intent: ToastIntentEnum.Success, message: '' })
  const [rating, setRating] = useState<number>(0)
  const [isClosing, setIsClosing] = useState(false)

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    setValue
  } = useForm<FeedbackFormData>({
    resolver: yupResolver(feedbackSchema),
    defaultValues: {
      satisfactionScore: rating,
      comment: '',
      question: surveyQuestion
    }
  })

  const onSubmit = async (data: FeedbackFormData) => {
    try {
      const response = await submitFeedback(data)
      if (response.success) {
        setToastInfo({
          show: true,
          intent: ToastIntentEnum.Success,
          message: 'Your feedback has been submitted'
        })

        setIsClosing(true)

        setTimeout(() => {
          setShowForm(false)
          setIsClosing(false)
        }, 1500)
      } else {
        setToastInfo({
          show: true,
          intent: ToastIntentEnum.Error,
          message: 'There was an error sending your feedback'
        })
      }
    } catch (error) {
      console.log('Error submitting feedback:', error)
      setToastInfo({
        show: true,
        intent: ToastIntentEnum.Error,
        message: 'There was an error sending your feedback'
      })
    }
  }

  return (
    <>
      <ModalWrapper>
        <Dialog
          open={true}
          onOpenChange={(event, data) => setShowForm(data.open)}
        >
          <DialogSurface>
            <FormContainer>
              <DialogTitle>Feedback Form</DialogTitle>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.5rem' }}>
                {surveyQuestion}
              </p>

              {/* Satisfaction Star Rating */}
              <FormGroup>
                <Label size="large">Satisfaction</Label>
                <StarsContainer>
                  {[...Array(MAX_STARS)].map((_, index) => (
                    <FontAwesomeIcon
                      key={index}
                      icon={faStar}
                      size="lg"
                      color={index < rating ? '#FFB800' : 'gray'}
                      onClick={() => {
                        setRating(index + 1)
                        setValue('satisfactionScore', index + 1, {
                          shouldValidate: true
                        })
                      }}
                    />
                  ))}
                </StarsContainer>
                {errors.satisfactionScore && (
                  <p style={{ color: 'red' }}>
                    {errors.satisfactionScore.message}
                  </p>
                )}
              </FormGroup>

              {/* Comment Field */}
              <FormGroup>
                <Label htmlFor="comment" size="large">
                  Comment
                </Label>
                <Controller
                  name="comment"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      id="comment"
                      required
                      size="large"
                      rows={6}
                      placeholder="Enter your comment here"
                      style={{ marginTop: '0.5rem' }}
                    />
                  )}
                />
                {errors.comment && (
                  <p style={{ color: 'red' }}>{errors.comment.message}</p>
                )}
              </FormGroup>

              <DialogActions>
                <Button
                  loading={isSubmitting || isClosing}
                  onClick={handleSubmit(onSubmit)}
                  text={'Submit Feedback'}
                />
                <Button onClick={() => setShowForm(false)} text={'Cancel'} />
              </DialogActions>
            </FormContainer>
          </DialogSurface>
        </Dialog>
      </ModalWrapper>

      {toastInfo.show && (
        <Toast triggerToast={toastInfo.intent} title={toastInfo.message} />
      )}
    </>
  )
}

export default FeedbackModal
